// @ts-check
// ── Global search (⌘K) ────────────────────────────────────────────────────
// MOVED from app.js unchanged (2026-10-12, sidebar build part 4; standing rule:
// extract from app.js what you touch, the move and the change in separate
// commits). The search window (#gs-modal-overlay, ⌘K) and the Dashboard's
// inline search bar: clients and check-ins from memory, messages from
// messages-search. app.js hands in its own client list and the check-in
// review's two functions; nothing else changed.
//
// "Search or ask ⌘K" (Clayton, 2026-10-12, sidebar build part 4): it also finds
// PAGES (read from the sidebar itself, so a page hidden there is never offered
// here), and it is the way to Atlas: an "Ask Atlas" row under every search, and
// a mic in the box. ⌘J is gone; ⌘K is the only shortcut.
// @ts-nocheck — moved as it was; typed when it is next changed.
import { escHtml, formatDate } from './utils.js';
import { highlightHtml } from './html-escape.js';
import * as _config from './config.js';
import { showView, showClientProfile } from './navigation.js';
import { isJarvisOpen, openAtlas } from './jarvis/index.js';

/**
 * Wire the search window and the Dashboard search bar. Call once at boot.
 * @param {{ getClients: () => any[], getCheckins?: () => any[], openCheckinView?: (id: string) => void }} deps
 */
export function initGlobalSearch({ getClients, getCheckins, openCheckinView }) {
  let _gsDebounce = null;
  let _gsResults = [];

  // Shared search logic for both inline bar and modal
  // Highlight matching text. MOVED to modules/html-escape.js (highlightHtml)
  // so the Inbox's message search uses the same one.
  function _hl(text, query) {
    return highlightHtml(text, query, 'class="gs-match"');
  }

  // Every page this coach can reach, read from the sidebar: its rows, Settings
  // and Status in the account menu, and the two pages that became tabs. A row
  // the sidebar hides (Booked or Scribe switched off) or a team member cannot
  // see (data-owner-only, hidden by body.member-mode) is left out the same way.
  function reachablePages() {
    const member = document.body.classList.contains('member-mode');
    const reachable = (el) => el && !el.hidden && !el.classList.contains('hidden')
      && !(member && el.closest('[data-owner-only]'));
    const labelOf = (el) => (el.querySelector('span')?.textContent || el.textContent || '').trim();
    const iconOf = (el) => el?.querySelector('svg')?.outerHTML || '';
    const pages = [];
    document.querySelectorAll('.sidebar-nav .sb-group').forEach((group) => {
      const where = group.querySelector('.sb-group-title')?.textContent.trim() || '';
      group.querySelectorAll('.nav-btn[data-view]').forEach((b) => {
        if (reachable(b)) pages.push({ view: b.dataset.view, label: labelOf(b), where, icon: iconOf(b) });
      });
    });
    document.querySelectorAll('#sb-account-menu .nav-btn[data-view]').forEach((b) => {
      if (reachable(b)) pages.push({ view: b.dataset.view, label: labelOf(b), where: 'Account menu', icon: iconOf(b) });
    });
    [['.cal-tab[data-view="sessions"]', 'sessions', 'Sessions', 'calendar'],
     ['[data-builder-tab="automations"]', 'automations', 'Automations', 'builder']].forEach(([sel, view, label, home]) => {
      const homeRow = document.querySelector(`.sidebar-nav .nav-btn[data-view="${home}"]`);
      if (reachable(document.querySelector(sel)) && reachable(homeRow)) {
        pages.push({ view, label, where: `A tab on ${labelOf(homeRow)}`, icon: iconOf(homeRow) });
      }
    });
    return pages;
  }

  function globalSearch(query, resultsEl) {
    if (!query || query.length < 2) {
      resultsEl.innerHTML = '';
      resultsEl.classList.add('hidden');
      _gsResults = [];
      return;
    }

    const q = query.toLowerCase().trim();
    let html = '';
    _gsResults = [];

    // 0. Pages (from the sidebar, instant)
    const pageMatches = reachablePages().filter(p => p.label.toLowerCase().includes(q)).slice(0, 4);
    if (pageMatches.length > 0) {
      html += '<div class="gs-category">Pages</div>';
      pageMatches.forEach(p => {
        _gsResults.push({ type: 'page', view: p.view });
        // p.icon is the sidebar row's own SVG from index.html, never typed text.
        html += `<div class="gs-result" data-gs-idx="${_gsResults.length - 1}">
          <div class="gs-result-icon gs-result-icon--page">${p.icon}</div>
          <div class="gs-result-info">
            <div class="gs-result-title">${_hl(p.label, q)}</div>
            <div class="gs-result-sub">${escHtml(p.where)}</div>
          </div>
        </div>`;
      });
    }

    // 1. Clients (in-memory, instant)
    const clientMatches = (getClients() || []).filter(c => {
      const haystack = [c.name, c.firstName, c.lastName, c.email, c.phone, c.status,
        c.serviceType, c.city, c.instagramHandle, ...(c.tags || [])].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    }).slice(0, 6);

    if (clientMatches.length > 0) {
      html += '<div class="gs-category">Clients</div>';
      clientMatches.forEach(c => {
        const initials = (c.firstName || c.name || '?')[0].toUpperCase();
        const statusCls = { active: 'text-success', cancelled: 'text-danger', paused: 'text-warning' }[c.status] || '';
        _gsResults.push({ type: 'client', id: c.id });
        html += `<div class="gs-result" data-gs-idx="${_gsResults.length - 1}">
          <div class="gs-result-icon gs-result-icon--client">${escHtml(initials)}</div>
          <div class="gs-result-info">
            <div class="gs-result-title">${_hl(c.name || '', q)}</div>
            <div class="gs-result-sub">${_hl(c.email || c.phone || '', q)}${c.serviceType ? ' · ' + escHtml(c.serviceType) : ''}</div>
          </div>
          <span class="gs-result-badge ${statusCls}">${escHtml((c.status || '').charAt(0).toUpperCase() + (c.status || '').slice(1))}</span>
        </div>`;
      });
    }

    // 2. Check-ins (from checkin-review module, in-memory)
    const allCheckins = typeof getCheckins === 'function' ? getCheckins() : [];
    if (allCheckins.length > 0) {
      const ciMatches = allCheckins.filter(ci => {
        const client = (getClients() || []).find(x => x.id === ci.client_id);
        const name = client ? (client.name || '').toLowerCase() : '';
        return name.includes(q);
      }).slice(0, 4);
      if (ciMatches.length > 0) {
        html += '<div class="gs-category">Check-ins</div>';
        ciMatches.forEach(ci => {
          const client = (getClients() || []).find(x => x.id === ci.client_id);
          const name = client?.name || 'Unknown';
          const status = ci.data?.status || 'pending';
          const statusLabel = status === 'approved' ? 'Reviewed' : 'Pending';
          _gsResults.push({ type: 'checkin', id: ci.id });
          html += `<div class="gs-result" data-gs-idx="${_gsResults.length - 1}">
            <div class="gs-result-icon gs-result-icon--checkin">CI</div>
            <div class="gs-result-info">
              <div class="gs-result-title">${_hl(name, q)}</div>
              <div class="gs-result-sub">Week of ${ci.week_of ? formatDate(ci.week_of) : '—'} · ${statusLabel}</div>
            </div>
          </div>`;
        });
      }
    }

    // Ask Atlas: the last row, or the only one (already picked, so Enter asks)
    // when nothing matched. Atlas gets the words as typed, not lowercased.
    const asked = query.trim();
    _gsResults.push({ type: 'atlas', text: asked });
    const atlasRow = `<div class="gs-result gs-result--atlas${html ? '' : ' gs-active'}" data-gs-idx="${_gsResults.length - 1}">
          <div class="gs-result-icon gs-result-icon--atlas"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z"/><path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z"/></svg></div>
          <div class="gs-result-info">
            <div class="gs-result-title">Ask Atlas</div>
            <div class="gs-result-sub">“${escHtml(asked)}”</div>
          </div>
        </div>`;

    // Show results
    resultsEl.innerHTML = html + atlasRow;
    resultsEl.classList.remove('hidden');

    // 3. Messages (API, debounced) — append after client results
    if (q.length >= 3) {
      clearTimeout(_gsDebounce);
      _gsDebounce = setTimeout(async () => {
        try {
          const supabase = _config.getSupabase();
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) return;
          const resp = await fetch(`/.netlify/functions/messages-search?q=${encodeURIComponent(q)}&limit=5`, {
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
          if (!resp.ok) return;
          const res = await resp.json();
          if (res?.messages?.length && !resultsEl.classList.contains('hidden')) {
            let msgHtml = '<div class="gs-category">Messages</div>';
            res.messages.forEach(m => {
              const name = m.clientName || (getClients() || []).find(x => x.id === m.client_id)?.name || 'Unknown';
              const preview = (m.body || '').replace(/<[^>]*>/g, '').slice(0, 80);
              _gsResults.push({ type: 'message', clientId: m.client_id });
              msgHtml += `<div class="gs-result" data-gs-idx="${_gsResults.length - 1}">
                <div class="gs-result-icon gs-result-icon--message">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                </div>
                <div class="gs-result-info">
                  <div class="gs-result-title">${_hl(name, q)}</div>
                  <div class="gs-result-sub">${_hl(preview, q)}</div>
                </div>
              </div>`;
            });
            // Above Ask Atlas, which stays the last row.
            const atlas = resultsEl.querySelector('.gs-result--atlas');
            if (atlas) atlas.insertAdjacentHTML('beforebegin', msgHtml);
            else resultsEl.insertAdjacentHTML('beforeend', msgHtml);
          }
        } catch (err) { console.warn('[global-search] message search error:', err); }
      }, 300);
    }
  }

  // Navigate to a search result
  function navigateResult(result) {
    closeGlobalSearch();
    if (result.type === 'client') {
      showClientProfile(result.id);
    } else if (result.type === 'checkin') {
      showView('checkins');
      setTimeout(() => {
        if (typeof openCheckinView === 'function') openCheckinView(result.id);
      }, 150);
    } else if (result.type === 'message') {
      // Same hop the client list's chat button makes: the Inbox, then that client's chat.
      window.openInlineChatFromList(result.clientId);
    } else if (result.type === 'page') {
      showView(result.view);
    } else if (result.type === 'atlas') {
      openAtlas({ text: result.text });
    }
  }

  // Close all search UI
  function closeGlobalSearch() {
    const dashResults = document.getElementById('dashboard-search-results');
    const dashInput = document.getElementById('dashboard-search-input');
    const modalOverlay = document.getElementById('gs-modal-overlay');
    const modalInput = document.getElementById('gs-modal-input');
    const modalResults = document.getElementById('gs-modal-results');
    if (dashResults) { dashResults.classList.add('hidden'); dashResults.innerHTML = ''; }
    if (dashInput) dashInput.value = '';
    if (modalOverlay) modalOverlay.classList.add('hidden');
    if (modalInput) modalInput.value = '';
    if (modalResults) modalResults.innerHTML = '';
    _gsResults = [];
  }

  // Keyboard navigation within results. The highlighted row on screen is the
  // one Enter opens: messages arrive late and go in above Ask Atlas, so a
  // remembered position could point at a different row than the coach sees.
  function handleSearchKeydown(e, resultsEl) {
    const items = [...resultsEl.querySelectorAll('.gs-result')];
    if (!items.length) return;
    const lit = items.findIndex(el => el.classList.contains('gs-active'));
    const light = (i) => {
      items.forEach((el, n) => el.classList.toggle('gs-active', n === i));
      items[i]?.scrollIntoView?.({ block: 'nearest' });
    };

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      light(Math.min(lit + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      light(Math.max(lit - 1, 0));
    } else if (e.key === 'Enter' && lit >= 0 && _gsResults[parseInt(items[lit].dataset.gsIdx)]) {
      e.preventDefault();
      navigateResult(_gsResults[parseInt(items[lit].dataset.gsIdx)]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeGlobalSearch();
    }
  }

  // ── Wire up inline dashboard search bar ──────────────────────
  const dashInput = document.getElementById('dashboard-search-input');
  const dashResults = document.getElementById('dashboard-search-results');
  if (dashInput && dashResults) {
    dashInput.addEventListener('input', () => globalSearch(dashInput.value, dashResults));
    dashInput.addEventListener('keydown', (e) => handleSearchKeydown(e, dashResults));
    dashResults.addEventListener('click', (e) => {
      const item = e.target.closest('.gs-result');
      if (!item) return;
      const idx = parseInt(item.dataset.gsIdx);
      if (_gsResults[idx]) navigateResult(_gsResults[idx]);
    });
    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.global-search-bar')) {
        dashResults.classList.add('hidden');
        dashResults.innerHTML = '';
      }
    });
  }

  // ── Wire up global ⌘K modal ──────────────────────────────────
  const modalOverlay = document.getElementById('gs-modal-overlay');
  const modalInput = document.getElementById('gs-modal-input');
  const modalResults = document.getElementById('gs-modal-results');

  document.addEventListener('keydown', (e) => {
    // A review login (Meta's or Zoom's) has no search and no Atlas: its sidebar hides the button, and this the key.
    if (window._reviewMode) return;
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      // ⚠️ RULING J64: ⌘K is Search's alone; Atlas has no shortcut at all now
      // (2026-10-12) and opens from here. But while its bar is already open
      // (the wake word), ⌘K must not open Search behind it (Task 11 review,
      // Critical #1). Escape the bar first; ⌘K opens Search the moment it's
      // closed.
      if (isJarvisOpen()) return;
      if (modalOverlay?.classList.contains('hidden')) {
        modalOverlay.classList.remove('hidden');
        modalInput?.focus();
      } else {
        closeGlobalSearch();
      }
    }
    if (e.key === 'Escape' && !modalOverlay?.classList.contains('hidden')) {
      closeGlobalSearch();
    }
  });

  if (modalInput && modalResults) {
    modalInput.addEventListener('input', () => globalSearch(modalInput.value, modalResults));
    modalInput.addEventListener('keydown', (e) => handleSearchKeydown(e, modalResults));
    modalResults.addEventListener('click', (e) => {
      const item = e.target.closest('.gs-result');
      if (!item) return;
      const idx = parseInt(item.dataset.gsIdx);
      if (_gsResults[idx]) navigateResult(_gsResults[idx]);
    });
  }
  // The mic in the box opens Atlas listening.
  document.getElementById('gs-mic')?.addEventListener('click', () => {
    closeGlobalSearch();
    openAtlas();
  });
  if (modalOverlay) {
    modalOverlay.addEventListener('click', (e) => {
      if (e.target === modalOverlay) closeGlobalSearch();
    });
  }
}
